import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';

import { BOMRow, ExtractedTableType, TableRow } from '@frontend/api';

import {
  HeaderCellRender,
  ItemNoCellRender,
  ItemNoHeaderCellRender,
} from 'modules/extractedTables/cells';

/**
 * Builds a dictionary mapping BOM row IDs to their index in the flattened BOM data.
 * This is used to quickly look up the index of a BOM row given its ID.
 *
 * @param rows - The BOM rows to build the dictionary for.
 * @param parentPath - The parent path of the current rows, used to construct the full path.
 * @param startIndex - The starting index for the current set of rows.
 * @returns A tuple containing the dictionary and the next starting index.
 */
const buildBomRowIdxByRowIdMap = (
  rows: BOMRow[],
  parentPath: string,
  startIndex: number,
): [Record<string, number>, number] => {
  let dict: Record<string, number> = {};
  let currentIndex = startIndex;

  rows.forEach((row, i) => {
    // For sibling #i (0-based), the display number is i+1
    const displayNum = i + 1;

    // If we're at top-level (parentPath==""), the new path is just "1","2", etc.
    // If we're in deeper levels, it's e.g. "1.2","1.3","2.1.1", etc.
    const childPath =
      parentPath === '' ? `${displayNum}` : `${parentPath}.${displayNum}`;

    dict[childPath] = currentIndex;
    currentIndex++;

    // Recurse for children
    if (row.children && row.children.length > 0) {
      const [childDict, nextIndex] = buildBomRowIdxByRowIdMap(
        row.children,
        childPath,
        currentIndex,
      );
      dict = { ...dict, ...childDict };
      currentIndex = nextIndex;
    }
  });

  return [dict, currentIndex];
};

export const useExtractedTableConfig = ({
  extractedTable,
}: {
  extractedTable: ExtractedTableType;
}) => {
  // Generate column config and data
  const { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId } =
    useMemo<{
      rawColumns: ColumnDef<TableRow>[];
      bomColumns: ColumnDef<BOMRow>[];
      rawData: TableRow[];
      bomData: BOMRow[];
      bomRowIdxsByRowId: Record<string, number>;
    }>(() => {
      const bomColumns: ColumnDef<BOMRow>[] = [];
      const bomData: BOMRow[] = [];
      const rawColumns: ColumnDef<TableRow>[] = [];
      const rawData: TableRow[] = [];

      /**
       * BOM view: structured columns from bomHeader/bomRows when present.
       *
       * RAW view: always mirrors the persisted table shape, independent of BOM.
       * - if one header row, use it as column headers and exclude it from data
       * - otherwise use unspecified column names and include header rows in data
       */
      if (extractedTable.bomHeader && extractedTable.bomRows) {
        bomColumns.push(
          {
            id: 'id',
            header: (props) =>
              ItemNoHeaderCellRender({
                headerName: 'Item No.',
                ...props,
              }),
            cell: ({ row, cell, table }) => {
              return ItemNoCellRender({ row, cell, table });
            },
          },
          {
            id: `partNumber`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Part Number',
                ...props,
              }),
            accessorFn: (row) => {
              return row.partNumber ?? '';
            },
          },
          {
            id: `revision`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Revision',
                ...props,
              }),
            accessorFn: (row) => {
              return row.revision ?? '';
            },
          },
          {
            id: `description`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Description',
                ...props,
              }),
            accessorFn: (row) => {
              return row.description ?? '';
            },
          },
          {
            id: `count`,
            header: (props) =>
              HeaderCellRender({
                headerName: 'Quantity',
                ...props,
              }),
            accessorFn: (row) => {
              return row.count;
            },
          },
        );

        bomData.push(...extractedTable.bomRows);
      }

      if (extractedTable.table.headerRows.length === 1) {
        const headerRow = extractedTable.table.headerRows[0];
        const columns: ColumnDef<TableRow>[] = headerRow.cells.map(
          (headerCell) => ({
            id: `dynamic_col_${headerCell.headerKey}`,
            header: (props) =>
              HeaderCellRender({
                headerName:
                  headerCell.content ?? `Column ${headerCell.headerKey}`,
                ...props,
              }),
            accessorFn: (row) => row.cells[headerCell.headerKey]?.content ?? '',
          }),
        );
        rawColumns.push(...columns);
        rawData.push(...extractedTable.table.rows);
      } else if (extractedTable.table.rows.length > 0) {
        const firstRow = extractedTable.table.rows[0];
        const columns: ColumnDef<TableRow>[] = firstRow.cells.map(
          (headerCell) => ({
            id: `dynamic_col_${headerCell.headerKey}`,
            header: (props) =>
              HeaderCellRender({
                headerName: `Column ${headerCell.columnIndex}`,
                ...props,
              }),
            accessorFn: (row) => row.cells[headerCell.headerKey]?.content ?? '',
          }),
        );
        rawColumns.push(...columns);
        rawData.push(
          ...extractedTable.table.headerRows,
          ...extractedTable.table.rows,
        );
      }

      // Create dictionary to map ids to row indices
      // NB: We start at 1, because index is root, and start at the children of the first BOM row because we don't want to include the root
      const [bomRowIdxsByRowId] = buildBomRowIdxByRowIdMap(
        bomData[0]?.children ?? [],
        '',
        1,
      );

      return { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId };
    }, [extractedTable]);

  return { rawColumns, bomColumns, rawData, bomData, bomRowIdxsByRowId };
};
